import { createContext, ReactNode, useContext } from "react";

import { useT } from "@/i18n";
import { Button, ButtonProps, DialogTrigger, Dropdown, IconButton, IconDots, IconPlus } from "@/ui";

const ManagerDisplayCardContext = createContext<{ t: ReturnType<typeof useT> } | null>(null);

const useManagerDisplayCard = () => {
  const context = useContext(ManagerDisplayCardContext);
  if (!context) {
    throw new Error("ManagerDisplayCard components must be used within ManagerDisplayCard");
  }
  return context;
};

/* Root */
type ManagerDisplayCardRootProps = {
  children: ReactNode;
};

const ManagerDisplayCardRoot = ({ children }: ManagerDisplayCardRootProps) => {
  const t = useT();
  return (
    <ManagerDisplayCardContext.Provider value={{ t }}>
      <div className="p-4 bg-surface-2 border-surface-1 rounded-md flex flex-col gap-4">
        {children}
      </div>
    </ManagerDisplayCardContext.Provider>
  );
};

/* Header */
type ManagerDisplayCardHeaderProps = {
  label: string;
  description: string;
  addButton?: ReactNode;
};

const ManagerDisplayCardHeader = ({
  label,
  description,
  addButton,
}: ManagerDisplayCardHeaderProps) => {
  return (
    <div className="flex items-center gap-5 justify-between">
      <div className="flex flex-col gap-1">
        <p className="preset-label-lg text-new-primary">{label}</p>
        <p className="preset-body-md text-secondary">{description}</p>
      </div>
      {addButton}
    </div>
  );
};

type ManagerDisplayCardAddButtonProps = ButtonProps;

const ManagerDisplayCardAddButton = ({ children, ...props }: ManagerDisplayCardAddButtonProps) => {
  const { t } = useManagerDisplayCard();
  return (
    <Button variant="secondary" color="brand" size="lg" startIcon={IconPlus} {...props}>
      {children ?? t("general.actions.add")}
    </Button>
  );
};

type ManagerDisplayCardUserItemProps = {
  avatar: ReactNode;
  displayName: string;
  /** Subtle secondary line under the name (e.g. the manager's id). */
  secondaryLabel?: string;
  /** When omitted, the row is read-only (no actions menu). */
  dropdownItems?: ReactNode;
};

const ManagerDisplayCardUserItem = ({
  avatar,
  displayName,
  secondaryLabel,
  dropdownItems,
}: ManagerDisplayCardUserItemProps) => {
  const { t } = useManagerDisplayCard();

  return (
    <div className="flex items-center gap-5 justify-between">
      <div className="flex gap-2 items-center">
        {avatar}
        <div className="flex flex-col">
          <p className="preset-body-lg text-new-primary">{displayName}</p>
          {secondaryLabel !== undefined && secondaryLabel !== "" && (
            <p className="preset-body-sm text-secondary">{secondaryLabel}</p>
          )}
        </div>
      </div>
      {dropdownItems !== undefined && (
        <DialogTrigger>
          <IconButton
            label={t("general.components.iconButton.labels.moreActions")}
            variant="tertiary"
            color="muted"
            size="md"
            icon={IconDots}
            onPress={() => {}}
          />
          <Dropdown label={t("general.components.dropdown.labels.actions")}>
            {dropdownItems}
          </Dropdown>
        </DialogTrigger>
      )}
    </div>
  );
};

type ManagerDisplayCardListProps = {
  children: ReactNode;
};

const ManagerDisplayCardList = ({ children }: ManagerDisplayCardListProps) => {
  return <div className="flex flex-col gap-4">{children}</div>;
};

/* Compound component export */
export const ManagerDisplayCard = Object.assign(ManagerDisplayCardRoot, {
  Header: ManagerDisplayCardHeader,
  AddButton: ManagerDisplayCardAddButton,
  List: ManagerDisplayCardList,
  UserItem: ManagerDisplayCardUserItem,
});
